// Updated copy of design system ColorPicker component
// needed there to be able to use gradient picker
// remove when design system is updated
import React, { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { LayerProps, mergeRefs, TriggerProps, useLayer } from 'react-laag';
import { PlacementType } from 'react-laag/dist/PlacementType';
import { token } from '@bynder/design-system';
import { type Color } from './types';
import { InternalColorPicker } from './components/InternalColorPicker';
import { ColorPickerProvider } from './components/InternalColorPickerContext';

export interface ColorPickerTriggerProps extends TriggerProps {
    isOpen: boolean;
    onClick: () => void;
    'aria-haspopup': boolean;
    'aria-expanded': boolean;
    'aria-controls': string;
}

type Props = {
    value: Color;
    onChange: (newValue: Color) => void;
    setColorDragging: (state: boolean) => void;
    isColorDragging: boolean;
    isColorTypeChangeAllowed?: boolean;
    /** Component which triggers the opening/closing of the colorpicker. It can be `Input` or `Button`. If nothing is provided, it will render a standalone colorpicker. */
    trigger?: (props: ColorPickerTriggerProps) => ReactNode;
    isLeftPositionAllowed?: boolean;
};

export function ColorPicker(props: Props) {
    const { trigger, value: _value, onChange: _onChange, setColorDragging, isColorDragging, ...restProps } = props;

    const [isOpen, setIsOpen] = useState(false);
    const layerRef = useRef<HTMLDivElement | null>(null);
    const timer = useRef<NodeJS.Timeout | number | null>(null);

    const triggerRef = useRef<HTMLElement | null>(null);
    const dropdownId = useId('colorpicker');
    const zIndex = 20000;

    const possiblePlacements: PlacementType[] = ['bottom-start', 'top-start', 'bottom-end', 'top-end'];

    if (props.isLeftPositionAllowed) {
        possiblePlacements.push('left-end');
    }

    const {
        renderLayer,
        triggerProps: { ref: triggerLayerRef },
        layerProps,
    } = useLayer({
        isOpen,
        onOutsideClick: undefined,
        triggerOffset: 8,
        containerOffset: 16,
        auto: true,
        container: getFirstShadowElement(triggerRef.current),
        placement: 'bottom-start',
        preferX: 'left',
        preferY: 'bottom',
        possiblePlacements,
    });

    fixLayerProps(layerProps);

    const cb = useCallback(() => {
        if (isOpen) {
            setIsOpen(false);
        }
    }, [isOpen]);

    useKeyDown('Escape', cb);

    useEffect(() => {
        if (!isOpen || isInShadow(triggerRef.current)) {
            return;
        }

        const handleMouseUp = () => {
            timer.current = setTimeout(() => {
                setColorDragging(false);

                if (typeof timer.current === 'number') {
                    clearTimeout(timer.current);
                    timer.current = null;
                }
            }, 10);
        };

        const handleMouseDown = (e: MouseEvent) => {
            if (typeof timer.current === 'number') {
                clearTimeout(timer.current);
                timer.current = null;
            }

            const target = e.target as Node;
            const clickedInside = layerRef.current?.contains(target) || triggerRef.current?.contains(target);

            if (clickedInside) {
                setColorDragging(true);
            }

            if (clickedInside) {
                setIsOpen(true);
            } else {
                setColorDragging(false);
                setIsOpen(false);
            }
        };

        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('mousedown', handleMouseDown);

        return () => {
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('mousedown', handleMouseDown);
        };
    }, [isOpen, setColorDragging]);

    return trigger ? (
        <ColorPickerProvider isColorDragging={isColorDragging}>
            {trigger({
                isOpen,
                onClick: () => setIsOpen((prev) => !prev),
                'aria-haspopup': true,
                'aria-expanded': isOpen,
                'aria-controls': dropdownId,
                ref: mergeRefs(triggerLayerRef, triggerRef),
            })}

            {isOpen &&
                renderLayer(
                    <Container
                        id={dropdownId}
                        $zIndex={zIndex}
                        {...layerProps}
                        {...restProps}
                        ref={mergeRefs(layerRef, layerProps.ref)}
                    >
                        <ColorPickerCard>
                            <InternalColorPicker
                                defaultValue={props.value}
                                onChange={props.onChange}
                                isColorTypeChangeAllowed={props.isColorTypeChangeAllowed}
                            />
                        </ColorPickerCard>
                    </Container>,
                )}
        </ColorPickerProvider>
    ) : (
        <ColorPickerCard {...restProps}>
            <InternalColorPicker
                defaultValue={props.value}
                onChange={props.onChange}
                isColorTypeChangeAllowed={props.isColorTypeChangeAllowed}
            />
        </ColorPickerCard>
    );
}

const Container = styled.div<{ $zIndex: number }>`
    z-index: ${(props) => props.$zIndex};
`;

const ColorPickerCard = styled.div`
    width: 296px;
    border-radius: ${token.radiusBase};
    background-color: ${token.gray10};
    box-shadow: ${token.elevationStatic}, ${token.elevation2};

    .react-colorful {
        width: 100%;
        height: auto;
    }

    .react-colorful__pointer {
        width: 16px;
        height: 16px;
        border-width: 4px;
        box-shadow: 0 0 0 1px ${token.gray200a};
        cursor: pointer;
    }

    .react-colorful__pointer-fill {
        box-shadow: inset ${token.elevation1};
    }
`;

function isInShadow(node?: Element | null): boolean {
    if (!node) {
        return false;
    }

    return node.getRootNode() instanceof ShadowRoot;
}

function getFirstShadowElement(node?: Element | null): HTMLElement | undefined {
    if (!node || !isInShadow(node)) {
        return;
    }

    const shadowRoot = node?.getRootNode() as ShadowRoot;

    const firstElementChild = Array.from(shadowRoot.children).find((el) => el.tagName !== 'STYLE');

    return firstElementChild as HTMLElement;
}

function useKeyDown(key: KeyboardEvent['key'], callback: (event: KeyboardEvent) => void) {
    useEffect(() => {
        function handler(event: KeyboardEvent) {
            if (event.key === key) {
                callback(event);
            }
        }

        window.addEventListener('keydown', handler);

        return () => {
            window.removeEventListener('keydown', handler);
        };
    }, [callback, key]);
}

function fixLayerProps(layerProps: LayerProps) {
    if (process.env.NODE_ENV === 'test') {
        layerProps.style = {
            ...layerProps.style,
            top: Number.isInteger(layerProps.style.top) ? layerProps.style.top : 0,
            left: Number.isInteger(layerProps.style.left) ? layerProps.style.left : 0,
        };
    }
}

function useId(prefix: string): string {
    return useMemo(() => prefix + '-' + uuidv4(), [prefix]);
}
